import depsloader from "../../../depsloader";

const DEFAULT_SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const DEFAULT_REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const REQUEST_TIMEOUT = 25000;

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this._seq = 0;
    this._activeSource = null;
  }

  _config() {
    if (typeof window !== "undefined" && window.SwGeocoderConfig) {
      return window.SwGeocoderConfig;
    }
    return {};
  }

  _key() {
    const c = this._config();
    return c && typeof c.key === "string" ? c.key : "";
  }

  _searchUrl() {
    const c = this._config();
    return c && c.searchUrl ? c.searchUrl : DEFAULT_SEARCH_URL;
  }

  _reverseUrl() {
    const c = this._config();
    return c && c.reverseUrl ? c.reverseUrl : DEFAULT_REVERSE_URL;
  }

  cancelAutocomplete() {
    // Laufende Anfrage invalidieren und abbrechen, ohne haengendes Promise.
    this._seq++;
    if (this._activeSource != null) {
      this._activeSource.cancel("superseded");
      this._activeSource = null;
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state) {
      p.push(g.state);
    }

    if (g.country) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  _geoaddress(l) {
    if (!l || !l.properties || !l.geometry || !Array.isArray(l.geometry.coordinates)) {
      return null;
    }

    const p = l.properties;
    const g = {};
    const type = p.type || null;

    // Strasse: bei type "street" bzw. Legacy osm_key "highway" traegt name den Strassennamen.
    let name = p.name != null ? p.name : null;
    let street = p.street != null ? p.street : null;
    if (!street && (type === "street" || p.osm_key === "highway")) {
      street = name;
      name = null;
    }

    if (street && p.housenumber) {
      g.street = street;
      g.number = p.housenumber;
    } else if (street) {
      g.street = street;
    }

    if (p.postcode) {
      g.zip = p.postcode;
    }

    const city = p.city != null ? p.city : null;
    let district = p.district != null ? p.district : null;

    // Legacy-Fallback fuer Bezirk ueber osm_key/osm_value, falls kein district-Feld vorliegt.
    if (!district && name && city && name !== city) {
      if (p.osm_key === "boundary" || (p.osm_key === "place" && p.osm_value === "suburb")) {
        district = name;
      }
    }

    if (city) {
      g.city = city;
      if (district) {
        g.district = district;
      }
    } else if (name) {
      // Keine Stadtangabe: Feature-Namen als Beschriftung erhalten, nicht als Stadt erfinden.
      g.city = name;
    }

    g.lat = l.geometry.coordinates[1];
    g.lng = l.geometry.coordinates[0];

    g.state = p.state != null ? p.state : null;
    g.country = p.country != null ? p.country : null;

    g.text = this._geoaddressText(g);

    return g;
  }

  geocode(params) {
    // Vorherige Anfrage abbrechen und Sequenz erhoehen (nur juengste Antwort zaehlt).
    if (this._activeSource != null) {
      this._activeSource.cancel("superseded");
      this._activeSource = null;
    }
    const seq = ++this._seq;

    const key = this._key();
    if (!key) {
      // Ohne Key kontrolliert ohne Anfrage enden.
      return Promise.resolve([]);
    }
    params.append("key", key);

    return new Promise(resolve => {
      const source = this.axios.CancelToken.source();
      this._activeSource = source;
      const timer = setTimeout(() => source.cancel("timeout"), REQUEST_TIMEOUT);

      this.axios
        .get(this._searchUrl(), {
          cancelToken: source.token,
          params
        })
        .then(res => {
          if (seq !== this._seq) {
            resolve([]);
            return;
          }
          const results = [];
          const features = res && res.data && res.data.features;
          if (Array.isArray(features)) {
            features.forEach(l => {
              const geoaddress = this._geoaddress(l);
              if (geoaddress) {
                results.push(geoaddress);
              }
            });
          }
          resolve(results);
        })
        .catch(() => {
          // Abbruch oder Fehler: leeres Ergebnis, kein haengendes Promise, keine veralteten Treffer.
          resolve([]);
        })
        .finally(() => {
          clearTimeout(timer);
          if (this._activeSource === source) {
            this._activeSource = null;
          }
        });
    });
  }

  autocomplete(q) {
    const params = new URLSearchParams();
    params.append("q", q);
    params.append("lang", "de");
    params.append("limit", "10");
    params.append("osm_tag", "!place:state");
    params.append("osm_tag", "!place:house");
    //params.append("osm_tag", "!boundary:protected_area");
    params.append("osm_tag", "place");
    params.append("osm_tag", "boundary");

    return this.geocode(params);
  }

  reverse(lat, lng) {
    const key = this._key();
    if (!key) {
      // Ohne Key kontrolliert ohne Anfrage enden.
      return Promise.reject(new Error("no maps key"));
    }

    const nlat = Number(lat);
    const nlon = Number(lng);
    if (
      !Number.isFinite(nlat) ||
      !Number.isFinite(nlon) ||
      nlat < -90 ||
      nlat > 90 ||
      nlon < -180 ||
      nlon > 180
    ) {
      return Promise.reject(new Error("invalid coordinates"));
    }

    return new Promise((resolve, reject) => {
      const source = this.axios.CancelToken.source();
      const timer = setTimeout(() => source.cancel("timeout"), REQUEST_TIMEOUT);

      const params = new URLSearchParams();
      params.append("lat", String(nlat));
      params.append("lon", String(nlon));
      params.append("lang", "de");
      params.append("key", key);

      this.axios
        .get(this._reverseUrl(), {
          cancelToken: source.token,
          params
        })
        .then(res => {
          const features = res && res.data && res.data.features;
          if (Array.isArray(features) && features.length > 0) {
            const geoaddress = this._geoaddress(features[0]);
            if (geoaddress) {
              resolve(geoaddress);
              return;
            }
          }
          reject(new Error("invalid data"));
        })
        .catch(err => {
          reject(err);
        })
        .finally(() => {
          clearTimeout(timer);
        });
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    });
  });
}

export { GeocoderFactory };

export default {};
